import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ToastProvider } from '@vd/ui';
import { ThemeProvider } from './app/theme';
import { ThemeSync } from './app/ThemeSync';
import { AuthProvider } from './features/auth/AuthProvider';
import { AppRouter } from './app/router';
import { ErrorBoundary } from './app/ErrorBoundary';
import { DogfoodWidget } from './features/widget/DogfoodWidget';
import { RecorderProvider } from './features/recorder/RecorderProvider';
import { NavigationGuardProvider } from './app/NavigationGuard';
import { TourProvider } from './features/tours/TourProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => {
        // Don't retry auth/forbidden errors
        if (err && typeof err === 'object' && 'status' in err) {
          const status = (err as { status: number }).status;
          if (status === 401 || status === 403 || status === 404) return false;
        }
        return count < 2;
      },
    },
  },
});

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              {/* Pulls the signed-in user's theme onto the page, and pushes
                  changes back. See ThemeSync for why it is a component. */}
              <ThemeSync />
              <RecorderProvider>
              {/* Opt into the v7 behaviors now so React Router stops
                  dev-warning on every render. Both flags are safe to
                  enable on v6 — they're forward-compat shims. */}
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <NavigationGuardProvider>
                  {/* Inside the router: tour steps belong to routes (TOADO-971). */}
                  <TourProvider>
                    <AppRouter />
                  </TourProvider>
                </NavigationGuardProvider>
              </BrowserRouter>
              {/* Toado-on-Toado: only renders when VITE_TOADO_WIDGET_KEY is set. */}
              <DogfoodWidget />
              </RecorderProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
